import * as React from 'react';
import { Component, Fragment } from 'react';
import { action, observable, computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { top } from '../utils/customPropStyles';
import Icon from './Icon';
import Translate from './Translate';
import ClickLayer from './ClickLayer';
import { formatDateTime } from '../utils/formatter';
import { Format } from '../models/configuration/Format';
import { DataStore } from '../stores';

const DateSelectorWrapper = styled<any, any>('div')`
    ${propStyles};
`;

DateSelectorWrapper.defaultProps = {
    position: 'relative',
    marginX: 'xs',
    marginY: 'sm',
};

const DateSelectorLabel = styled<any, any>('label')`
    ${propStyles};
`;

DateSelectorLabel.defaultProps = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    fontSize: 'xs',
    fontFamily: 'body',
    fontWeight: 'normal',
    color: 'body',
};

const DateSelectorInputWrapper = styled<any, any>('div')`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

DateSelectorInputWrapper.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    padding: 'sm',
    fontSize: 'xs',
    fontFamily: 'body',
    fontWeight: 'normal',
    color: 'body',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    hover: {
        borderColor: 'primary',
    },
    selected: {
        color: 'body',
        borderColor: 'primary',
    },
};

const DateSelectorLabelText = styled<any, any>('div')`
    ${propStyles};
`;

DateSelectorLabelText.defaultProps = {
    marginX: 'xs',
};

const DateSelectorInput = styled<any, any>('input')`
    ${propStyles};
    &::placeholder {
        color: currentColor;
    }
`;

DateSelectorInput.defaultProps = {
    fontSize: 'inherit',
    fontFamily: 'inherit',
    fontWeight: 'inherit',
    color: 'inherit',
    outline: 'none',
    borderStyle: 'none',
};

const DateSelectorFilterClearButton = styled<any, any>('button')`
    ${propStyles};
`;

DateSelectorFilterClearButton.defaultProps = {
    display: 'flex',
    flex: '0 0 auto',
    position: 'relative',
    zIndex: 'dropdown',
    cursor: 'pointer',
    outline: 'none',
    borderStyle: 'none',
};

const DateSelectorDropdown = styled<any, any>('div')`
    ${propStyles};
    ${top};
`;

DateSelectorDropdown.defaultProps = {
    position: 'absolute',
    top: 'calc(100% + 1px)',
    left: '0',
    right: '0',
    zIndex: 'dropdown',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    boxShadow: 'sm',
};

const DateSelectorList = styled<any, any>('ol')`
    ${propStyles};
    list-style: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

DateSelectorList.defaultProps = {
    maxHeight: '300px',
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingY: 'sm',
};

const DateSelectorListItem = styled<any, any>('li')`
    ${propStyles};
`;

DateSelectorListItem.defaultProps = {
    fontSize: 'xs',
    fontFamily: 'body',
    fontWeight: 'normal',
    color: 'body',
    bgColor: 'white',
    paddingX: 'md',
    paddingY: 'sm',
    cursor: 'pointer',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
    hover: {
        bgColor: 'gray200',
    },
    selected: {
        color: 'white',
        bgColor: 'primary',
    },
};

interface ChartDateSelectorProps {
    theme?: any;
    label: string;
    value: string;
    values: Array<string>;
    selectDate: (date: string) => void;
    valueFormat: Format;
    locale: string;
    dataStore: DataStore;
}

interface FormattedValue {
    date: string;
    formattedDate: string;
}

@inject('translations')
@observer
class ChartDateSelector extends Component<ChartDateSelectorProps> {
    @observable expanded = false;
    @observable dateFilter = '';
    @observable selectedDateListItemOffset = 0;

    @computed
    get formattedValue(): string {
        return formatDateTime({
            locale: this.props.locale,
            options: this.props.valueFormat.options,
            value: this.props.value,
        });
    }

    @computed
    get formattedValues(): Array<FormattedValue> {
        return this.props.values
            .map(value => ({
                date: value,
                formattedDate: formatDateTime({
                    value,
                    locale: this.props.locale,
                    options: this.props.valueFormat.options,
                }),
            }))
            .filter(value =>
                value.formattedDate
                    .toLowerCase()
                    .includes(this.dateFilter.toLowerCase())
            );
    }

    @action
    toggleExpanded = () => {
        this.expanded = !this.expanded;
        this.dateFilter = '';
    };

    @action
    setDateFilter = (value: string) => {
        this.dateFilter = value;
    };

    @action
    setDate = (value: string) => {
        this.toggleExpanded();
        this.props.selectDate(value);
    };

    dateList = (node: HTMLElement) => {
        if (node) {
            node.scrollTop = this.selectedDateListItemOffset;
        }
    };

    @action
    selectedDateListItem = (node: HTMLElement) => {
        if (node) {
            this.selectedDateListItemOffset = node.offsetTop;
        }
    };

    render() {
        const { theme, label, value } = this.props;

        return (
            <DateSelectorWrapper {...theme.DateSelectorWrapper}>
                <DateSelectorLabel {...theme.DateSelectorLabel}>
                    <DateSelectorLabelText {...theme.DateSelectorLabelText}>
                        <Translate>{label}</Translate>
                    </DateSelectorLabelText>
                    <DateSelectorInputWrapper
                        data-selected={this.expanded}
                        {...theme.DateSelectorInputWrapper}
                    >
                        <DateSelectorInput
                            placeholder={this.formattedValue}
                            value={this.dateFilter}
                            onChange={e => this.setDateFilter(e.target.value)}
                            onFocus={this.toggleExpanded}
                            {...theme.DateSelectorInput}
                        />
                        {this.dateFilter.length > 0 && (
                            <DateSelectorFilterClearButton
                                onClick={() => this.setDateFilter('')}
                                {...theme.DateSelectorFilterClearButton}
                            >
                                <Icon
                                    fileName="CloseIcon"
                                    height="1em"
                                    width="1em"
                                    {...theme.DateSelectorFilterClearButtonIcon}
                                />
                            </DateSelectorFilterClearButton>
                        )}
                    </DateSelectorInputWrapper>
                </DateSelectorLabel>
                {this.expanded && (
                    <Fragment>
                        <ClickLayer onClick={this.toggleExpanded} />
                        <DateSelectorDropdown {...theme.DateSelectorDropdown}>
                            <DateSelectorList
                                {...theme.DateSelectorList}
                                innerRef={this.dateList}
                            >
                                {this.formattedValues.map((v, i) => {
                                    const isSelected = value === v.date;
                                    return (
                                        <DateSelectorListItem
                                            key={v.date}
                                            innerRef={
                                                isSelected
                                                    ? this.selectedDateListItem
                                                    : null
                                            }
                                            data-alternated={
                                                i % 2 ? true : false
                                            }
                                            data-selected={isSelected}
                                            onClick={() => this.setDate(v.date)}
                                            {...theme.DateSelectorListItem}
                                        >
                                            {v.formattedDate}
                                        </DateSelectorListItem>
                                    );
                                })}
                            </DateSelectorList>
                        </DateSelectorDropdown>
                    </Fragment>
                )}
            </DateSelectorWrapper>
        );
    }
}

export default withTheme(ChartDateSelector);



// WEBPACK FOOTER //
// ./src/Components/ChartDateSelector.tsx